import { apiService } from './apiService';

export interface Tag {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
  email_count?: number;
}

export interface TagCreate {
  name: string;
}

export interface TagUpdate {
  name?: string;
}

export interface EmailGroup {
  email_name: string;
  client_count: number;
  clients: string[];
  client_ids: number[];
  first_email_id: number;
  email_ids: number[];  // All email IDs for this group
  total_opens: number;
  total_clicks: number;
  open_rate: number;
  click_rate: number;
  tags: Tag[];
}

export interface TagAnalytics {
  tag_id: number;
  tag_name: string;
  total_emails: number;
  total_opens: number;
  total_clicks: number;
  average_open_rate: number;
  average_click_rate: number;
  email_groups: EmailGroup[];
}

export interface EmailTagAssignment {
  email_id: number;
  tag_id: number;
  assigned_at: string;
}

// AI Analysis Interfaces
export interface AITagSuggestion {
  name: string;
  type: 'hotel_chain' | 'resort' | 'destination' | 'tour' | 'airline' | 'cruise' | 'travel_type' | 'other';
  is_existing: boolean;
  existing_match?: string;
  confidence: string;
  context: string;
}

export interface AIAnalysisResult {
  success: boolean;
  tags: AITagSuggestion[];
  analysis: string;
  confidence: string;
  destinations: AITagSuggestion[];
  travel_types: AITagSuggestion[];
  error?: string;
}

export interface EmailForAIAnalysis {
  email_name: string;
  subject: string;
  client_count: number;
  clients: string[];
  client_ids: number[];
  email_ids: number[];
  first_email_id: number;
  scheduled_date: string | null;
  existing_tags: {
    id: number;
    name: string;
  }[];
}

export interface AIAnalysisResponse {
  email_id: number;
  email_name: string;
  analysis_result: AIAnalysisResult;
}

export interface TagCreationResponse {
  success: boolean;
  tag_id: number;
  tag_name: string;
  tag_created: boolean;
  xref_created: boolean;
  email_id: number;
}

export interface TagAssignmentResponse {
  success: boolean;
  tag_id: number;
  tag_name: string;
  email_id: number;
  xref_created: boolean;
  message?: string;
}

// Tag CRUD operations
export const getTags = async (): Promise<Tag[]> => {
  try {
    const response = await apiService.get<Tag[]>('/api/tags');
    return response.data;
  } catch (error) {
    console.error('Error fetching tags:', error);
    throw error;
  }
};

export const getTagsForClient = async (clientId: number): Promise<Tag[]> => {
  try {
    const response = await apiService.get<Tag[]>(`/api/tags/client/${clientId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching tags for client:', error);
    throw error;
  }
};

export const createTag = async (tag: TagCreate): Promise<Tag> => {
  try {
    const response = await apiService.post<Tag>('/api/tags', tag);
    return response.data;
  } catch (error) {
    console.error('Error creating tag:', error);
    throw error;
  }
};

export const updateTag = async (id: number, tag: TagUpdate): Promise<Tag> => {
  try {
    const response = await apiService.put<Tag>(`/api/tags/${id}`, tag);
    return response.data;
  } catch (error) {
    console.error('Error updating tag:', error);
    throw error;
  }
};

export const deleteTag = async (id: number): Promise<void> => {
  try {
    await apiService.delete(`/api/tags/${id}`);
  } catch (error) {
    console.error('Error deleting tag:', error);
    throw error;
  }
};

export const getTag = async (id: number): Promise<Tag> => {
  try {
    const response = await apiService.get<Tag>(`/api/tags/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching tag:', error);
    throw error;
  }
};

// Email-Tag assignment operations
export const assignEmailToTag = async (tagId: number, emailId: number): Promise<EmailTagAssignment> => {
  try {
    const response = await apiService.post<EmailTagAssignment>(`/api/tags/${tagId}/emails/${emailId}`, {});
    return response.data;
  } catch (error) {
    console.error('Error assigning email to tag:', error);
    throw error;
  }
};

export const removeEmailFromTag = async (tagId: number, emailId: number): Promise<void> => {
  try {
    await apiService.delete(`/api/tags/${tagId}/emails/${emailId}`);
  } catch (error) {
    console.error('Error removing email from tag:', error);
    throw error;
  }
};

// New email name-based assignment operations (more efficient)
export const assignEmailNameToTag = async (tagId: number, emailName: string): Promise<any> => {
  try {
    const response = await apiService.post<any>(`/api/tags/${tagId}/emails/name`, {
      email_name: emailName
    });
    return response.data;
  } catch (error) {
    console.error('Error assigning email name to tag:', error);
    throw error;
  }
};

export const removeEmailNameFromTag = async (tagId: number, emailName: string): Promise<any> => {
  try {
    const response = await apiService.delete<any>(`/api/tags/${tagId}/emails/name/${encodeURIComponent(emailName)}`);
    return response.data;
  } catch (error) {
    console.error('Error removing email name from tag:', error);
    throw error;
  }
};

export const removeAllEmailsFromTag = async (tagId: number): Promise<any> => {
  try {
    const response = await apiService.delete<any>(`/api/tags/${tagId}/emails/all`);
    return response.data;
  } catch (error) {
    console.error('Error removing all emails from tag:', error);
    throw error;
  }
};

export const getTagEmails = async (tagId: number): Promise<any[]> => {
  try {
    const response = await apiService.get<any[]>(`/api/tags/${tagId}/emails`);
    return response.data;
  } catch (error) {
    console.error('Error fetching tag emails:', error);
    throw error;
  }
};

// Analytics operations
export const getEmailGroupsByTag = async (
  dateRange: string = 'last_30_days',
  startDate?: string,
  endDate?: string
): Promise<EmailGroup[]> => {
  try {
    const params: any = { date_range: dateRange };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    const response = await apiService.get<EmailGroup[]>('/api/tags/analytics/email-groups', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching email groups:', error);
    throw error;
  }
};

export const getTagAnalytics = async (
  dateRange: string = 'last_30_days',
  startDate?: string,
  endDate?: string,
  selectedTags?: number[],
  clientId?: number
): Promise<TagAnalytics[]> => {
  try {
    const params: any = { date_range: dateRange };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (selectedTags && selectedTags.length > 0) params.tag_ids = selectedTags.join(',');
    if (clientId) params.client_id = clientId;
    
    const response = await apiService.get<TagAnalytics[]>('/api/tags/analytics/tag-performance', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching tag analytics:', error);
    throw error;
  }
};

// AI Analysis operations
export const getEmailsForAIAnalysis = async (dateRange: string): Promise<EmailForAIAnalysis[]> => {
  try {
    const response = await apiService.get<EmailForAIAnalysis[]>(`/api/tags/ai/emails?date_range=${dateRange}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching emails for AI analysis:', error);
    throw error;
  }
};

export const getEmailHTMLForAIAnalysis = async (emailName: string): Promise<EmailForAIAnalysis> => {
  try {
    const response = await apiService.get<EmailForAIAnalysis>(`/api/tags/ai/emails/${encodeURIComponent(emailName)}/html`);
    return response.data;
  } catch (error) {
    console.error('Error fetching email HTML for AI analysis:', error);
    throw error;
  }
};

export const analyzeEmailWithAI = async (emailName: string): Promise<AIAnalysisResponse> => {
  try {
    const response = await apiService.post<AIAnalysisResponse>(`/api/tags/ai/analyze?email_name=${encodeURIComponent(emailName)}`);
    return response.data;
  } catch (error) {
    console.error('Error analyzing email with AI:', error);
    throw error;
  }
};

export const analyzeMultipleEmailsWithAI = async (emailIds: number[]): Promise<AIAnalysisResponse[]> => {
  try {
    const response = await apiService.post<AIAnalysisResponse[]>(`/api/tags/ai/analyze-batch`, null, {
      params: { email_ids: emailIds }
    });
    return response.data;
  } catch (error) {
    console.error('Error analyzing multiple emails with AI:', error);
    throw error;
  }
};

export const createTagFromAIAnalysis = async (tagName: string, emailName: string): Promise<TagCreationResponse> => {
  try {
    const response = await apiService.post<TagCreationResponse>(`/api/tags/ai/create-tag`, null, {
      params: { 
        tag_name: tagName,
        email_name: emailName
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating tag from AI analysis:', error);
    throw error;
  }
};

export const assignExistingTagToEmail = async (tagId: number, emailName: string): Promise<TagAssignmentResponse> => {
  try {
    const response = await apiService.post<TagAssignmentResponse>(`/api/tags/ai/assign-existing-tag`, null, {
      params: { 
        tag_id: tagId,
        email_name: emailName
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error assigning existing tag to email:', error);
    throw error;
  }
}; 